import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { KioskShell } from "../components/KioskShell";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const MAX_POLLS = 12;
const POLL_INTERVAL = 2000;

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", attempts: 0, data: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Missing session id" });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({ status: "timeout", data });
          return;
        }
        setState((s) => ({ ...s, status: "polling", attempts, data }));
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        if (attempts >= MAX_POLLS) {
          setState({ status: "error", error: e.message });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <KioskShell showExit>
      <section className="max-w-2xl mx-auto px-6 py-20">
        <div className="surface p-12 rounded-md text-center">
          {state.status === "polling" && (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-6 animate-spin" style={{ color: "var(--wakzy-gold)" }} />
              <h1 className="font-heading text-3xl mb-3">Confirming your reservation…</h1>
              <p className="text-slate-400">Checking with Stripe ({state.attempts}/{MAX_POLLS})</p>
            </>
          )}
          {state.status === "paid" && (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto mb-6" style={{ color: "var(--wakzy-gold)" }} />
              <h1 className="font-heading text-4xl mb-3">Reservation confirmed.</h1>
              <p className="text-slate-300 max-w-md mx-auto mb-2">
                We've placed a hold on your vehicle. A sales associate will reach out shortly to finalize your purchase.
              </p>
              <p className="text-sm text-slate-500 mb-8">
                Deposit: ${(state.data.amount_total / 100).toFixed(2)} {state.data.currency?.toUpperCase()}
              </p>
              <Button onClick={() => navigate(`/kiosk/checkout/${state.data.vehicle_id}`)} className="btn-cta rounded-sm h-12 px-8" data-testid="continue-purchase-btn">
                Continue to purchase paperwork <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <div className="mt-3">
                <Button variant="outline" onClick={() => navigate("/kiosk")} className="rounded-sm bg-transparent" data-testid="back-to-inventory-btn">
                  Back to inventory
                </Button>
              </div>
            </>
          )}
          {(state.status === "expired" || state.status === "timeout") && (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-6 text-amber-400" />
              <h1 className="font-heading text-3xl mb-3">
                {state.status === "expired" ? "Session expired" : "Still processing"}
              </h1>
              <p className="text-slate-400 mb-6">
                Please check your email for confirmation, or try again.
              </p>
              <Button onClick={() => navigate("/kiosk")} className="btn-cta rounded-sm">Back to inventory</Button>
            </>
          )}
          {state.status === "error" && (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-6 text-red-400" />
              <h1 className="font-heading text-3xl mb-3">Something went wrong</h1>
              <p className="text-slate-400 mb-6">{state.error}</p>
              <Button onClick={() => navigate("/kiosk")} className="btn-cta rounded-sm">Back to inventory</Button>
            </>
          )}
        </div>
      </section>
    </KioskShell>
  );
}
